import { formatLogMessage } from '@maestro/core/logger/json-log-formatter'
import { sanitizeLogs } from '@maestro/core/logger/log-sanitizer'

export const CONSOLE_METHODS = [
  'log',
  'info',
  'warn',
  'error',
  'debug',
] as const

/**
 * Check if expandable objects mode should be enabled.
 * When enabled, objects are passed directly to console methods
 * so they appear as expandable/inspectable in debug consoles.
 *
 * Auto-detects debugger via inspector.url() or --inspect flags.
 * Can be manually controlled via LOG_EXPANDABLE_OBJECTS=true/false
 */
function isExpandableObjectsEnabled(): boolean {
  if (typeof process === 'undefined') {
    return false
  }
  // Manual override via environment variable
  const envOverride = process.env?.LOG_EXPANDABLE_OBJECTS
  if (envOverride === 'true' || envOverride === '1') {
    return true
  }
  if (envOverride === 'false' || envOverride === '0') {
    return false
  }

  // Check if debugger is attached using inspector.url() - most reliable
  try {
    // Use eval to hide from bundlers (inspector is Node.js only)
    const inspector = eval("require('inspector')")
    if (typeof inspector?.url === 'function' && inspector.url()) {
      return true
    }
  } catch {
    // Inspector not available
  }

  // Fallback checks
  const execArgv = process.execArgv?.join(' ') || ''
  if (/--inspect/.test(execArgv)) {
    return true
  }
  const nodeOptions = process.env?.NODE_OPTIONS || ''
  if (/--inspect/.test(nodeOptions)) {
    return true
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (globalThis as any).v8debug === 'object') {
    return true
  }
  return false
}

// Cache the check at module load
const expandableObjectsEnabled = isExpandableObjectsEnabled()

export const METHOD_TO_LEVEL_MAP: Record<
  (typeof CONSOLE_METHODS)[number],
  LogLevelName
> = {
  log: 'info',
  info: 'info',
  warn: 'warn',
  error: 'error',
  debug: 'debug',
} as const

type LogLevelName = 'debug' | 'info' | 'warn' | 'error'

const LOG_LEVELS: Record<LogLevelName, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
}

function getEnvLogLevel(): LogLevelName {
  const level = (process.env.LOG_LEVEL || 'debug').toLowerCase()
  if (level in LOG_LEVELS) {
    return level as LogLevelName
  }
  return 'debug'
}

const CURRENT_LOG_LEVEL = LOG_LEVELS[getEnvLogLevel()]

/**
 * Overrides the default console methods to log messages in JSON format.
 * 1. Sanitizes the log arguments to remove sensitive data.
 * 2. Formats the log message as a JSON object.
 *
 * When a debugger is attached (VS Code, --inspect), skips JSON formatting
 * and passes objects directly for expandable inspection in debug console.
 *
 * Only works on server-side. No-op on client (browser).
 */
export function overrideConsoleLogger() {
  // Never override console on the client (browser)
  if (typeof window !== 'undefined') {
    return
  }

  CONSOLE_METHODS.forEach((method) => {
    const original = console[method]
    const levelName = METHOD_TO_LEVEL_MAP[method]
    const levelValue = LOG_LEVELS[levelName]

    console[method] = (...args: unknown[]) => {
      try {
        if (levelValue < CURRENT_LOG_LEVEL) {
          return
        }

        // When debugger is attached, pass sanitized args directly
        // so objects are expandable in VS Code Debug Console
        if (expandableObjectsEnabled) {
          const sanitizedArgs = sanitizeLogs(args)
          original.call(console, ...sanitizedArgs)
          return
        }

        const output = formatLogMessage({ method, args })

        original.call(console, output)
      } catch {
        // Logger must never crash the application
        original.call(console, ...args)
      }
    }
  })
}
